/**
 * Admin / Manager user manual — EN + PT.
 *
 * Content is kept in-code (JS) instead of markdown so we can:
 *   * benefit from language switching without file loaders
 *   * ship the docs as part of the JS bundle (no extra fetches)
 *   * easily add per-module `data-testid` attributes and links to real pages
 */

export const adminManual = {
  en: {
    dashboard: {
      title: "Dashboard",
      path: "/",
      overview: "The dashboard is the operational cockpit — you land here after login. It surfaces the metrics that most affect cash flow and field productivity: open service calls, unpaid invoices, scheduled work today, active timeclocks, and outstanding purchase orders.",
      features: [
        "Real-time counters that refresh on every navigation.",
        "Quick-jump tiles to the modules with pending work.",
        "Overdue invoices highlighted in red; upcoming due dates in amber.",
        "Live status of the daily crew (clocked-in vs assigned).",
      ],
      tasks: [
        { title: "Review today's operations", steps: [
          "Open the sidebar → Dashboard.",
          "Scan the 'Scheduled today' tile — click it to jump directly to today's Schedule.",
          "Check 'Overdue invoices' — click through to Receivables to send reminders.",
          "Confirm 'Active timeclocks' matches the number of techs actually in the field.",
        ]},
        { title: "Investigate an unusual metric", steps: [
          "Any tile is a link — click to drill into that module.",
          "Use the filters inside the target module (date range, status, technician) to isolate.",
        ]},
      ],
      tips: [
        "Use the dashboard at 8:00 AM and 4:00 PM to catch problems before they compound.",
        "Overdue invoices > 30 days should be your first priority — cash flow beats new work.",
      ],
    },

    inbox: {
      title: "Inbox",
      path: "/inbox",
      overview: "The Inbox is where notifications, mentions, and system events land. Anything that requires your attention — a client submitted a ticket, a PO needs approval, a project is over budget — is queued here so nothing gets lost.",
      features: [
        "Unified feed of tickets, POs, project alerts, and system notices.",
        "Unread counter shown in the top-right bell icon.",
        "Filter by type and by module.",
        "Mark-as-read one-by-one or in bulk.",
      ],
      tasks: [
        { title: "Triage your inbox each morning", steps: [
          "Sidebar → Inbox.",
          "Filter to 'Unread'.",
          "Click each item to open the related record; take action (respond, approve, escalate).",
          "Mark cleared items as read.",
        ]},
      ],
      tips: [
        "Turn on browser notifications so you're pinged even when the tab is in the background.",
        "Inbox is the single-source-of-truth — don't rely on emails for internal handoffs.",
      ],
    },

    tickets: {
      title: "Service Calls (Tickets)",
      path: "/tickets",
      overview: "Service Calls are the atomic unit of field work. Each ticket ties together a client, a property (and optional unit), a scheduled date, one or more technicians, work steps, and any receipts/material requests generated in the field.",
      features: [
        "Property + unit picker (properties without units are supported).",
        "Multi-technician assignment with a primary responsible manager.",
        "Priority levels (low → urgent).",
        "Service steps checklist tracked by the tech.",
        "Photos, notes, purchase notes with AI receipt scan.",
        "Automatic Daily Report and Material Request integration.",
        "Automatic address inheritance from unit → property fallback.",
        "Estimate + invoice workflow (requires_estimate flag).",
      ],
      tasks: [
        { title: "Create a Service Call for a property with no units", steps: [
          "Sidebar → Service Calls → 'New'.",
          "Fill in Title and Description.",
          "In the two-column selector, choose the Property from the first dropdown.",
          "Leave the Unit dropdown showing 'No units — use property address'.",
          "Assign one or more technicians, pick priority.",
          "Save — the address and property_name are inherited automatically.",
        ]},
        { title: "Attach an emergency receipt from the field", steps: [
          "Open the ticket → 'Add purchase note'.",
          "Snap the receipt, AI extracts amount + supplier.",
          "System auto-creates a Material Request marked 'purchased'.",
          "Purchasing can later mark it 'paid' from the Purchasing module.",
        ]},
        { title: "Schedule a ticket", steps: [
          "Open the ticket → set 'Scheduled date'.",
          "Alternatively drag from 'Not scheduled' to a weekday in the Schedule module.",
        ]},
      ],
      tips: [
        "Assign a JMO manager as 'Responsible' — they get the notifications for that job.",
        "Use priority 'urgent' sparingly — abuse dilutes the signal.",
        "Photos before/after every job are non-negotiable for client disputes.",
      ],
      troubleshooting: [
        "Address blank in Schedule card: make sure the property has an address on record.",
        "Tenant contact missing: it now pulls from unit or property tenant fields automatically.",
      ],
    },

    schedule: {
      title: "Schedule",
      path: "/schedule",
      overview: "The Schedule module lays out the weekly grid of what each technician is doing, day by day. It's the single view your dispatcher uses to load-balance the field.",
      features: [
        "Weekly grid by technician × day.",
        "Drag-and-drop from 'Not scheduled' onto a day.",
        "Cards show address (or property) + unit label.",
        "Priority color coding.",
        "Side panel with ticket details.",
      ],
      tasks: [
        { title: "Assign an unscheduled ticket", steps: [
          "Sidebar → Schedule.",
          "Locate the ticket in the 'Not scheduled' rail on the right.",
          "Drag it onto the technician + day slot.",
          "The ticket's scheduled_date is updated automatically.",
        ]},
        { title: "Move a ticket to another day", steps: [
          "Grab the card and drop it on the new day.",
          "Communicate the change to the tech (they'll see it in their mobile view).",
        ]},
      ],
      tips: [
        "Set realistic time windows — avoid stacking 5 jobs on one tech per day unless they're all in the same block.",
        "Use the address label to spot geographic clustering opportunities.",
      ],
    },

    projects: {
      title: "Projects (WBS)",
      path: "/projects",
      overview: "Projects manages large, multi-phase engagements with Work Breakdown Structure (WBS) and progress billing. Unlike Service Calls (single-visit repairs), a Project has a contract value, tracks % complete per line item, invoices in phases, and integrates with Purchasing/Materials at the item level.",
      features: [
        "Multi-level WBS with Group / Line / Sub-line hierarchy.",
        "Progress executed vs progress billed columns for each line.",
        "'Use Progress' button that auto-calculates unbilled percentages.",
        "Contract Total lock — variance recorded as loss, contract stays fixed.",
        "Material Request creation directly from a line item.",
        "Client approval token for external stakeholders.",
        "PDF proposals + progress reports (branded).",
      ],
      tasks: [
        { title: "Create a new Project", steps: [
          "Sidebar → Projects → 'New Project'.",
          "Fill client, jobsite, contract number, start date.",
          "Add WBS groups, lines and sub-lines. Each line has qty, unit price, subtotal.",
          "Save. Send Proposal to client for signature.",
        ]},
        { title: "Bill progress on a project", steps: [
          "Open project → 'New Invoice'.",
          "Click 'Use Progress' — the modal auto-fills unbilled executed percentages per line.",
          "Adjust any line, save the invoice.",
          "Send to client from Receivables.",
        ]},
      ],
      tips: [
        "Always update executed % weekly — it's the source of truth for billing.",
        "Never edit contract total after signing — record variances as separate lines.",
      ],
    },

    estimates: {
      title: "Estimates",
      path: "/estimates",
      overview: "Estimates are pre-approved proposals for work. They can stand alone or be attached to a ticket that requires_estimate. Once approved, they can be converted directly into an invoice or a project.",
      features: [
        "Line-item pricing with markup/margin controls.",
        "Optional Material lines pulled from Materials catalog.",
        "PDF export with JMO header.",
        "Send-to-client with tokenized approval link.",
        "Status: draft → sent → approved / rejected.",
      ],
      tasks: [
        { title: "Convert approved estimate to invoice", steps: [
          "Open the approved Estimate.",
          "Click 'Create Invoice' — it copies all lines to Receivables.",
        ]},
      ],
      tips: [
        "Save estimate templates for recurring work (annual maintenance, etc.).",
      ],
    },

    receivables: {
      title: "Receivables (Invoices)",
      path: "/receivables",
      overview: "Receivables is the AR ledger — all invoices sent to clients live here. Track outstanding balances, send reminders, record payments, and export statements.",
      features: [
        "Aging report (0-30 / 31-60 / 61-90 / 90+).",
        "Send invoice PDF via email in one click.",
        "Record payments and mark invoices as paid.",
        "Auto-links to the parent Ticket or Project.",
      ],
      tasks: [
        { title: "Record a client payment", steps: [
          "Open the invoice → 'Record Payment'.",
          "Enter amount, method (check / ACH / card), date.",
          "Invoice moves to 'paid' status; residual becomes a partial payment if not full.",
        ]},
      ],
      tips: [
        "Send reminders at day 25 (before default), day 40, and day 55. Escalate at 60+.",
      ],
    },

    crew: {
      title: "Crew on the Field",
      path: "/crew",
      overview: "Live view of who's clocked in, where they are, and what they're working on. Perfect for the dispatcher and the boss.",
      features: [
        "Live status per technician (clocked in / on break / off).",
        "Assigned tickets today per tech.",
        "Total hours worked in the current pay period.",
      ],
      tasks: [
        { title: "Spot a tech who isn't working", steps: [
          "Sidebar → Crew.",
          "Filter to 'clocked out' — if they should be working, call them.",
        ]},
      ],
      tips: [
        "This module is a diagnostic — for real assignments use Schedule.",
      ],
    },

    timeclock: {
      title: "Time Clock",
      path: "/timeclock",
      overview: "Time Clock records every clock-in / clock-out for every user. It's the source data for Payroll (both HR and per-ticket) and for job costing.",
      features: [
        "Clock-in / clock-out by ticket or by project.",
        "Break tracking.",
        "Manual adjustments (admin only).",
        "Export per period.",
      ],
      tasks: [
        { title: "Edit a bad time entry", steps: [
          "Sidebar → Time Clock.",
          "Filter by employee + date.",
          "Click the entry → edit start/end/break → save.",
          "Audit trail keeps the previous values.",
        ]},
      ],
      tips: [
        "Enable auto clock-out at end of day so techs don't accidentally log 24-hour shifts.",
      ],
    },

    payroll: {
      title: "Payroll (Job Costing)",
      path: "/payroll",
      overview: "This Payroll module is different from HR Payroll — it groups technician hours per Job (ticket/project) for job costing. HR Payroll (in the HR module) is what actually pays employees.",
      features: [
        "Weekly grid by employee.",
        "Filter by pay period, employee.",
        "Export CSV.",
        "Print weekly PDF (hours per job).",
      ],
      tasks: [
        { title: "Generate the job-cost report for the week", steps: [
          "Sidebar → Payroll.",
          "Choose the week (← / This week / →).",
          "Click 'Print PDF' or 'Export CSV'.",
        ]},
      ],
      tips: [
        "Cross-check with HR Payroll totals — mismatches usually mean missing time entries.",
      ],
    },

    hr: {
      title: "HR Management",
      path: "/hr",
      overview: "The HR module manages employees, salary history, bonuses, reimbursements, and — most importantly — the weekly/monthly payroll projection. This is where the actual pay of every employee is computed.",
      features: [
        "Employee registry (salaried / hourly / subcontractor).",
        "Bonuses and reimbursements per employee (with status: pending / approved / paid).",
        "Weekly OR monthly payroll summary (weekly = salary/52; monthly = salary/12).",
        "Landscape PDF: consolidated weekly report (all employees on one page).",
        "Individual Payslip PDF (per employee, per week).",
        "CSV export.",
      ],
      tasks: [
        { title: "Generate the weekly payroll PDF", steps: [
          "Sidebar → HR → Payroll tab.",
          "Confirm the toggle is on 'Weekly' (default).",
          "Navigate weeks with ← / 'This week' / →.",
          "Click 'Weekly PDF' — the consolidated report downloads.",
        ]},
        { title: "Give each employee their payslip", steps: [
          "On the same Payroll tab, each row has a Payslip icon (last column).",
          "Click it — the individual payslip PDF downloads.",
          "Send it via your usual channel (email, print).",
        ]},
        { title: "Approve and pay a bonus", steps: [
          "HR → Bonuses tab → find the pending bonus.",
          "Click 'Approve'. Then when actually paid, click 'Mark paid' with the date.",
          "The bonus now appears in that week/month's payroll.",
        ]},
      ],
      tips: [
        "Weekly is what most hourly employees expect. Monthly is for salaried projection reports.",
        "Bonuses / reimbursements only enter the payroll total when marked 'paid' (not just approved).",
      ],
      troubleshooting: [
        "Employee shows $0 base as hourly: no time entries in the week. Check Time Clock.",
        "PDF cut off: fixed by using landscape orientation. If issue reappears, contact support.",
      ],
    },

    purchasing: {
      title: "Purchasing",
      path: "/purchasing",
      overview: "Purchasing is the workflow that turns a Material Request into a real purchase (PO, direct buy, or ledger entry). It's also the module where AP payments are posted to the Cash Book.",
      features: [
        "Direct Purchase (AI receipt scan, no MR needed).",
        "'Mark as Purchased' on Material Requests.",
        "'+ Pay' button — post payment to a Bank Account (Ledger).",
        "Supplier Autocomplete (universal component).",
        "PO email delivery.",
      ],
      tasks: [
        { title: "Log a direct purchase (no PO)", steps: [
          "Purchasing → '+ Direct Purchase'.",
          "Snap the receipt — AI extracts fields.",
          "Confirm supplier, amount, category.",
          "'+ Pay' immediately if it was cash/card.",
        ]},
        { title: "Pay an MR", steps: [
          "Material Requests → find MR → 'Mark as Purchased'.",
          "Click '+ Pay' → pick bank account → post.",
        ]},
      ],
      tips: [
        "Every purchase should eventually hit the Ledger — use '+ Pay' consistently.",
      ],
    },

    material_requests: {
      title: "Material Requests",
      path: "/material-requests",
      overview: "MRs are how techs and PMs request material without leaving the field. They flow through requested → approved → purchased → paid, with each step visible to Purchasing.",
      features: [
        "Requested from Tickets or Projects.",
        "Status: requested → approved → purchased → paid.",
        "Line items with qty + expected price.",
        "Attach to a project WBS line (job costing).",
        "AI receipt scan on 'Mark purchased'.",
      ],
      tasks: [
        { title: "Approve a pending MR", steps: [
          "Material Requests → filter 'requested'.",
          "Open MR → review lines → 'Approve'.",
          "Notify Purchasing.",
        ]},
      ],
    },

    materials: {
      title: "Materials Catalog",
      path: "/materials",
      overview: "The Materials catalog stores your standard SKUs — descriptions, unit, last known price, preferred supplier. Estimates and MRs pull from here.",
      features: [
        "Search, filter by category, sort by last price.",
        "Import from Excel.",
        "Per-material price history graph (linked from Price Trends).",
      ],
      tips: [
        "Keep descriptions consistent — abbreviations and inconsistencies kill autocomplete.",
      ],
    },

    material_trends: {
      title: "Material Price Trends",
      path: "/material-trends",
      overview: "This module visualizes how material prices have moved over time across the receipts and POs you've logged. Great for negotiating with suppliers.",
      features: [
        "Line chart per material.",
        "Compare multiple materials on one chart.",
        "Detects sudden price jumps ('anomaly').",
      ],
      tips: [
        "Review quarterly before contract renegotiations.",
      ],
    },

    suppliers: {
      title: "Suppliers",
      path: "/suppliers",
      overview: "Registry of your vendors. Used by Purchasing, MRs, and Estimates via the Supplier Autocomplete component.",
      features: [
        "Contact info, payment terms, primary category.",
        "'+ Create new' inline from any autocomplete.",
      ],
    },

    subcontractors: {
      title: "Subcontractors",
      path: "/subcontractors",
      overview: "Registry of external trades and specialists (insulation crews, restoration, electricians). Used by Daily Reports and Projects.",
      features: [
        "Contact + trade category.",
        "Search from Daily Report composer.",
      ],
    },

    equipment: {
      title: "Equipment",
      path: "/equipment",
      overview: "Fleet and gear registry — vehicles, boilers used for testing, trucks. Track assignment to a technician and maintenance history.",
      features: [
        "Assign equipment to a technician.",
        "Maintenance log.",
        "Gas / mileage entries (roadmap).",
      ],
    },

    financials: {
      title: "Financials (Reports)",
      path: "/financials",
      overview: "High-level financial reporting — revenue, expenses, cash balance. Sources data from invoices, POs, ledger.",
      features: [
        "P&L by month.",
        "Cash-in vs cash-out.",
        "By-client revenue breakdown.",
      ],
    },

    finance: {
      title: "Finance (Cash Book & Ledger)",
      path: "/finance",
      overview: "The transactional heart of Accounting — bank accounts, ledger entries, cash book. Every '+ Pay' from Purchasing lands here.",
      features: [
        "Multiple bank accounts (with running balance).",
        "Manual ledger entries.",
        "Reconciliation (roadmap).",
      ],
      tasks: [
        { title: "Add a new bank account", steps: [
          "Finance → Bank Accounts → '+ New'.",
          "Enter name, opening balance, currency.",
          "Save — this account now appears in every '+ Pay' modal.",
        ]},
      ],
    },

    clients: {
      title: "Clients",
      path: "/clients",
      overview: "The client registry. A client can own multiple properties (which own units). Each client has a portal login (optional).",
      features: [
        "Contact info + billing address.",
        "Linked properties + units.",
        "Client portal enable / disable.",
      ],
    },

    properties: {
      title: "Properties (Buildings)",
      path: "/properties",
      overview: "A property represents a physical location — a house, a building, a complex. Properties can have zero or more units (apartments, floors).",
      features: [
        "Address, city, ZIP.",
        "Owner (client) + optional tenant.",
        "Nested units.",
        "Import from Excel.",
      ],
      tasks: [
        { title: "Create a single-family home (no units)", steps: [
          "Properties → 'New'.",
          "Enter address + assign client.",
          "Skip 'Add unit'. Save.",
          "Service Calls will inherit this property's address directly.",
        ]},
      ],
    },

    daily_reports: {
      title: "Daily Reports",
      path: "(inside any Service Call or Project)",
      overview: "Daily Reports are the polished, client-ready record of a day's work. They auto-pull crew hours from Time Clock, list subcontractors, embed photos, format with AI, and produce a branded PDF that can be emailed to the client.",
      features: [
        "Employee search with automatic timeclock hours for the day.",
        "Subcontractor search + hours + worker count.",
        "Camera + Library photo picker (with automatic compression before upload).",
        "AI narrative generator (Anthropic).",
        "Branded PDF (navy JMO header + logo).",
        "Email delivery to client.",
      ],
      tasks: [
        { title: "Produce a daily report", steps: [
          "Open a Service Call or Project.",
          "Scroll to 'Daily Reports' → '+ Daily Report'.",
          "Enter narrative or paste raw notes.",
          "Add crew (auto-populates hours) and subcontractors.",
          "Add up to a dozen photos.",
          "Choose 'AI polish' → generate PDF → email to client.",
        ]},
      ],
      tips: [
        "Do the daily report before leaving the site — it's much harder to reconstruct later.",
        "Client-ready reports build trust and shortens payment cycles.",
      ],
    },

    map: {
      title: "Live Map",
      path: "/map",
      overview: "Live GPS map of your techs and active jobs. Optional — requires the mobile app tracker.",
      features: [
        "Tech marker refresh every 30s.",
        "Job pins colored by status.",
        "Click a marker → open the ticket.",
      ],
    },

    permissions: {
      title: "Permissions",
      path: "/permissions",
      overview: "Admin-only page to widen or narrow module access per role. Change 'crew:view' to false for PM to hide the Crew sidebar item without a deploy.",
      features: [
        "Matrix: roles × modules × actions (view, create, edit, delete).",
        "Reset to defaults per role.",
        "Live — takes effect on next reload.",
      ],
    },

    settings: {
      title: "Settings",
      path: "/settings",
      overview: "Company-wide settings: business info (used in PDFs), invoice numbering prefix, timezone, notification defaults.",
      features: [
        "Company name / address / phone (shown in Proposal & Payroll PDFs).",
        "Default currency and locale.",
        "Email templates.",
      ],
      tips: [
        "Keep Company Address current — it prints on every client-facing PDF.",
      ],
    },
  },

  // -----------------------------------------------------------------
  // Portuguese translation of the entire admin manual
  // -----------------------------------------------------------------
  pt: {
    dashboard: {
      title: "Dashboard",
      path: "/",
      overview: "O dashboard é o cockpit operacional — você aterrissa aqui após o login. Mostra as métricas que mais afetam o fluxo de caixa e a produtividade em campo: service calls abertos, faturas em aberto, trabalho agendado pra hoje, timeclocks ativos e POs pendentes.",
      features: [
        "Contadores em tempo real que atualizam a cada navegação.",
        "Tiles clicáveis para os módulos com trabalho pendente.",
        "Faturas vencidas em vermelho; próximas do vencimento em âmbar.",
        "Status ao vivo da equipe do dia (bateu ponto vs designados).",
      ],
      tasks: [
        { title: "Revisar a operação do dia", steps: [
          "Menu lateral → Dashboard.",
          "Olhe o tile 'Scheduled today' — clique pra ir direto ao Schedule do dia.",
          "Confira 'Overdue invoices' — clique para ir a Receivables e mandar cobrança.",
          "Confirme se 'Active timeclocks' bate com o número de técnicos que deveriam estar no campo.",
        ]},
        { title: "Investigar uma métrica estranha", steps: [
          "Todo tile é link — clique para ir ao módulo.",
          "Use os filtros internos (data, status, técnico) para isolar o caso.",
        ]},
      ],
      tips: [
        "Use o dashboard às 08:00 e às 16:00 pra pegar problemas antes que se acumulem.",
        "Faturas vencidas há mais de 30 dias devem ser a prioridade #1 — caixa vence trabalho novo.",
      ],
    },

    inbox: {
      title: "Inbox",
      path: "/inbox",
      overview: "O Inbox é onde caem notificações, menções e eventos do sistema. Tudo que precisa da sua atenção — cliente abriu ticket, PO precisa de aprovação, projeto passou do orçamento — fica enfileirado aqui pra não perder nada.",
      features: [
        "Feed unificado: tickets, POs, alertas de projeto, avisos.",
        "Contador de não-lidas no sino do canto superior direito.",
        "Filtro por tipo e por módulo.",
        "Marcar como lida em massa.",
      ],
      tasks: [
        { title: "Triagem do inbox toda manhã", steps: [
          "Menu → Inbox.",
          "Filtre para 'Não lidas'.",
          "Clique em cada item → abra o registro relacionado → tome ação (responder, aprovar, escalar).",
          "Marque como lida quando resolver.",
        ]},
      ],
      tips: [
        "Ative notificações do navegador para receber ping mesmo com aba em segundo plano.",
        "Inbox é a fonte da verdade — não confie em email pra handoffs internos.",
      ],
    },

    tickets: {
      title: "Service Calls (Chamados)",
      path: "/tickets",
      overview: "Service Calls são a unidade básica do trabalho em campo. Cada ticket amarra: cliente, propriedade (com ou sem unidade), data agendada, um ou mais técnicos, passos do serviço, e qualquer recibo/material request gerado em campo.",
      features: [
        "Seletor Property + Unit (property sem units funciona).",
        "Atribuição multi-técnico com um gestor JMO responsável.",
        "Prioridade (low → urgent).",
        "Checklist de passos rastreável pelo técnico.",
        "Fotos, notas e purchase notes com scan de recibo por AI.",
        "Integração automática com Daily Report e Material Request.",
        "Endereço herdado automaticamente da unit → property.",
        "Fluxo de estimativa + fatura (flag requires_estimate).",
      ],
      tasks: [
        { title: "Criar Service Call para property sem units", steps: [
          "Menu → Service Calls → 'New'.",
          "Preencha Title e Description.",
          "No seletor de duas colunas, escolha a Property no primeiro dropdown.",
          "Deixe o Unit dropdown mostrando 'No units — use property address'.",
          "Atribua técnicos, escolha prioridade.",
          "Salve — endereço e property_name herdam automaticamente.",
        ]},
        { title: "Anexar recibo de emergência do campo", steps: [
          "Abra o ticket → 'Add purchase note'.",
          "Tire foto do recibo, AI extrai valor + fornecedor.",
          "Sistema cria Material Request marcada como 'purchased'.",
          "Depois, Purchasing marca como 'paid' pelo módulo Purchasing.",
        ]},
        { title: "Agendar um ticket", steps: [
          "Abra o ticket → defina 'Scheduled date'.",
          "Ou arraste de 'Not scheduled' para o dia da semana no módulo Schedule.",
        ]},
      ],
      tips: [
        "Sempre atribua um manager JMO como 'Responsible' — ele recebe as notificações.",
        "Use prioridade 'urgent' com parcimônia — abuso dilui o sinal.",
        "Fotos antes/depois são obrigatórias em qualquer job (para disputa com cliente).",
      ],
      troubleshooting: [
        "Endereço em branco no Schedule: confira se a Property tem address cadastrado.",
        "Tenant contact ausente: agora vem automaticamente da unit ou da property.",
      ],
    },

    schedule: {
      title: "Schedule",
      path: "/schedule",
      overview: "O módulo Schedule mostra a grade semanal do que cada técnico faz, dia a dia. É a visão única que o despachante usa pra equilibrar a carga em campo.",
      features: [
        "Grade semanal técnico × dia.",
        "Drag-and-drop de 'Not scheduled' pra um dia.",
        "Cards mostram endereço (ou property) + unit label.",
        "Cores por prioridade.",
        "Painel lateral com detalhes do ticket.",
      ],
      tasks: [
        { title: "Alocar ticket não-agendado", steps: [
          "Menu → Schedule.",
          "Localize o ticket na coluna 'Not scheduled' à direita.",
          "Arraste pra o slot técnico + dia.",
          "scheduled_date é atualizada automaticamente.",
        ]},
        { title: "Mover ticket para outro dia", steps: [
          "Pegue o card e solte no novo dia.",
          "Avise o técnico (ele vê no app mobile).",
        ]},
      ],
      tips: [
        "Janelas de tempo realistas — evite empilhar 5 jobs em 1 técnico por dia se não forem próximos.",
        "Use o endereço pra identificar oportunidades de agrupamento geográfico.",
      ],
    },

    projects: {
      title: "Projects (WBS)",
      path: "/projects",
      overview: "Projects gerencia contratos grandes de múltiplas fases com Work Breakdown Structure (WBS) e progress billing. Diferente de Service Calls (reparos de 1 visita), um Project tem valor de contrato, rastreia % completo por linha, fatura por fases, e integra com Purchasing/Materials no nível do item.",
      features: [
        "WBS multi-nível: Group / Line / Sub-line.",
        "Colunas 'Progress Executed' vs 'Progress Billed' por linha.",
        "Botão 'Use Progress' calcula automaticamente % não-faturado.",
        "Lock de Contract Total — variância vira loss, contrato fica fixo.",
        "Material Request direto do item de linha.",
        "Token de aprovação para o cliente (link público).",
        "Propostas + progress reports em PDF (branded).",
      ],
      tasks: [
        { title: "Criar novo Project", steps: [
          "Menu → Projects → 'New Project'.",
          "Preencha cliente, jobsite, contract number, start date.",
          "Adicione grupos, linhas e sub-linhas ao WBS. Cada linha tem qty, unit price, subtotal.",
          "Salve. Envie Proposal pro cliente assinar.",
        ]},
        { title: "Faturar progresso do projeto", steps: [
          "Abra project → 'New Invoice'.",
          "Clique em 'Use Progress' — modal preenche % executado não-faturado por linha.",
          "Ajuste se necessário, salve.",
          "Envie ao cliente pelo Receivables.",
        ]},
      ],
      tips: [
        "Atualize o % executado semanalmente — é a fonte da verdade para faturamento.",
        "Nunca edite Contract Total depois de assinado — registre variância como linha separada.",
      ],
    },

    estimates: {
      title: "Estimates",
      path: "/estimates",
      overview: "Estimates são propostas pré-aprovadas. Podem ser standalone ou anexadas a um ticket que precisa de estimate. Após aprovado, converte direto em invoice ou project.",
      features: [
        "Preço por linha com markup/margin.",
        "Materials opcionais puxados do catálogo.",
        "PDF com cabeçalho JMO.",
        "Envio ao cliente com link de aprovação por token.",
        "Status: draft → sent → approved / rejected.",
      ],
      tasks: [
        { title: "Converter estimate aprovado em invoice", steps: [
          "Abra o Estimate aprovado.",
          "Clique 'Create Invoice' — copia todas as linhas pro Receivables.",
        ]},
      ],
      tips: [
        "Salve templates de estimate pra trabalhos recorrentes (manutenção anual etc.).",
      ],
    },

    receivables: {
      title: "Receivables (Faturas)",
      path: "/receivables",
      overview: "Receivables é o AR ledger — todas as faturas enviadas aos clientes ficam aqui. Rastreie saldos em aberto, envie lembretes, registre pagamentos, exporte extratos.",
      features: [
        "Aging report (0-30 / 31-60 / 61-90 / 90+).",
        "Enviar PDF por email em 1 clique.",
        "Registrar pagamento e marcar como paid.",
        "Auto-link ao ticket ou project pai.",
      ],
      tasks: [
        { title: "Registrar pagamento do cliente", steps: [
          "Abra a invoice → 'Record Payment'.",
          "Amount, método (check/ACH/card), data.",
          "Invoice vira 'paid'; se parcial, saldo continua aberto.",
        ]},
      ],
      tips: [
        "Envie lembretes no dia 25 (antes de vencer), 40 e 55. Escale a partir do 60.",
      ],
    },

    crew: {
      title: "Crew on the Field",
      path: "/crew",
      overview: "Visão ao vivo de quem bateu ponto, onde está, e no que está trabalhando. Ideal pro despachante e pro chefe.",
      features: [
        "Status ao vivo por técnico (clocked in / on break / off).",
        "Tickets alocados no dia por técnico.",
        "Total de horas do pay period.",
      ],
      tasks: [
        { title: "Detectar técnico que não está trabalhando", steps: [
          "Menu → Crew.",
          "Filtre 'clocked out' — se deveria estar trabalhando, ligue.",
        ]},
      ],
      tips: [
        "Este módulo é diagnóstico — para atribuir trabalho, use Schedule.",
      ],
    },

    timeclock: {
      title: "Time Clock",
      path: "/timeclock",
      overview: "Time Clock registra todo clock-in/clock-out. É a fonte de dados pro Payroll (HR e por-ticket) e pro job costing.",
      features: [
        "Clock-in / clock-out por ticket ou por project.",
        "Rastreio de breaks.",
        "Ajustes manuais (só admin).",
        "Export por período.",
      ],
      tasks: [
        { title: "Editar entrada errada", steps: [
          "Menu → Time Clock.",
          "Filtre por funcionário + data.",
          "Clique na entrada → edite start/end/break → salvar.",
          "Audit trail guarda valores anteriores.",
        ]},
      ],
      tips: [
        "Ative auto clock-out no fim do dia pra técnicos não logarem plantões de 24h por acidente.",
      ],
    },

    payroll: {
      title: "Payroll (Job Costing)",
      path: "/payroll",
      overview: "Este módulo Payroll é DIFERENTE do HR Payroll — ele agrupa horas do técnico por Job (ticket/project) pra custeio. O HR Payroll (dentro do módulo HR) é o que efetivamente paga funcionários.",
      features: [
        "Grade semanal por funcionário.",
        "Filtro por pay period, funcionário.",
        "Export CSV.",
        "Print PDF semanal (horas por job).",
      ],
      tasks: [
        { title: "Gerar relatório de custeio da semana", steps: [
          "Menu → Payroll.",
          "Escolha a semana (← / This week / →).",
          "Clique 'Print PDF' ou 'Export CSV'.",
        ]},
      ],
      tips: [
        "Confira contra os totais do HR Payroll — divergência quase sempre é lançamento faltando no timeclock.",
      ],
    },

    hr: {
      title: "HR Management",
      path: "/hr",
      overview: "O módulo HR gerencia funcionários, histórico salarial, bônus, reembolsos e — o mais importante — a projeção de payroll semanal/mensal. É aqui que o pagamento real de cada funcionário é calculado.",
      features: [
        "Cadastro de funcionários (salaried / hourly / subcontractor).",
        "Bônus e reembolsos por funcionário (status: pending / approved / paid).",
        "Payroll semanal OU mensal (semanal = salary/52; mensal = salary/12).",
        "PDF paisagem: consolidado semanal (todos os funcionários numa página).",
        "Payslip PDF individual (por funcionário, por semana).",
        "Export CSV.",
      ],
      tasks: [
        { title: "Gerar PDF semanal do payroll", steps: [
          "Menu → HR → aba Payroll.",
          "Confirme que o toggle está em 'Weekly' (default).",
          "Navegue com ← / 'This week' / →.",
          "Clique 'Weekly PDF' — baixa o consolidado.",
        ]},
        { title: "Entregar payslip individual pra cada funcionário", steps: [
          "Na mesma aba Payroll, cada linha tem ícone Payslip (última coluna).",
          "Clique — baixa o PDF individual daquela semana.",
          "Envie pelo canal habitual (email, impresso).",
        ]},
        { title: "Aprovar e pagar um bônus", steps: [
          "HR → aba Bonuses → localize o bônus pendente.",
          "Clique 'Approve'. Depois, quando efetivamente pago, 'Mark paid' com data.",
          "O bônus entra automaticamente no payroll da semana/mês em que foi pago.",
        ]},
      ],
      tips: [
        "Weekly é o que a maioria dos horistas espera. Monthly serve pra projeção dos salaried.",
        "Bônus/reembolsos só entram no total quando marcados 'paid' (não basta approved).",
      ],
      troubleshooting: [
        "Funcionário hourly com base $0: sem entradas de timeclock na semana. Confira Time Clock.",
        "PDF cortado: corrigido usando paisagem. Se voltar a acontecer, contate suporte.",
      ],
    },

    purchasing: {
      title: "Purchasing",
      path: "/purchasing",
      overview: "Purchasing é o workflow que transforma um Material Request em compra real (PO, direct buy, ou lançamento no ledger). Também é aqui que pagamentos AP são postados no Cash Book.",
      features: [
        "Direct Purchase (scan AI, sem MR).",
        "'Mark as Purchased' em Material Requests.",
        "Botão '+ Pay' — posta pagamento em uma Bank Account (Ledger).",
        "Supplier Autocomplete (componente universal).",
        "Envio de PO por email.",
      ],
      tasks: [
        { title: "Registrar compra direta (sem PO)", steps: [
          "Purchasing → '+ Direct Purchase'.",
          "Scan do recibo — AI extrai os campos.",
          "Confirme supplier, valor, categoria.",
          "'+ Pay' imediatamente se foi dinheiro/cartão.",
        ]},
        { title: "Pagar uma MR", steps: [
          "Material Requests → localize MR → 'Mark as Purchased'.",
          "Clique '+ Pay' → escolha bank account → postar.",
        ]},
      ],
      tips: [
        "Toda compra deve chegar no Ledger — use '+ Pay' consistentemente.",
      ],
    },

    material_requests: {
      title: "Material Requests",
      path: "/material-requests",
      overview: "MRs são como técnicos e PMs pedem material sem sair do campo. Fluem por: requested → approved → purchased → paid, cada passo visível pra Purchasing.",
      features: [
        "Criadas a partir de Tickets ou Projects.",
        "Status: requested → approved → purchased → paid.",
        "Linhas com qty + preço esperado.",
        "Anexar a uma linha WBS do project (job costing).",
        "Scan AI ao 'Mark purchased'.",
      ],
      tasks: [
        { title: "Aprovar MR pendente", steps: [
          "Material Requests → filtro 'requested'.",
          "Abra MR → revise linhas → 'Approve'.",
          "Avise Purchasing.",
        ]},
      ],
    },

    materials: {
      title: "Catálogo de Materiais",
      path: "/materials",
      overview: "O catálogo armazena seus SKUs padrão — descrições, unidade, último preço, fornecedor preferido. Estimates e MRs puxam daqui.",
      features: [
        "Busca, filtro por categoria, ordem por último preço.",
        "Importar do Excel.",
        "Gráfico de histórico de preços (via Price Trends).",
      ],
      tips: [
        "Descrições consistentes — abreviações e inconsistências matam o autocomplete.",
      ],
    },

    material_trends: {
      title: "Material Price Trends",
      path: "/material-trends",
      overview: "Este módulo visualiza como preços de materiais evoluíram no tempo (baseado em recibos e POs). Ótimo pra negociar com fornecedores.",
      features: [
        "Gráfico por material.",
        "Comparação de múltiplos materiais no mesmo gráfico.",
        "Detecção de saltos súbitos ('anomaly').",
      ],
      tips: [
        "Revise trimestralmente antes de renegociar contratos.",
      ],
    },

    suppliers: {
      title: "Fornecedores",
      path: "/suppliers",
      overview: "Cadastro dos seus fornecedores. Usado por Purchasing, MRs e Estimates via o Supplier Autocomplete.",
      features: [
        "Contato, prazo de pagamento, categoria principal.",
        "'+ Create new' inline de qualquer autocomplete.",
      ],
    },

    subcontractors: {
      title: "Subcontratados",
      path: "/subcontractors",
      overview: "Cadastro de terceiros e especialistas (crews de isolamento, restauração, eletricistas). Usado por Daily Reports e Projects.",
      features: [
        "Contato + categoria/trade.",
        "Busca no composer de Daily Report.",
      ],
    },

    equipment: {
      title: "Equipamentos",
      path: "/equipment",
      overview: "Cadastro de frota e ferramentas — veículos, boilers de teste, caminhões. Vincule ao técnico e mantenha histórico de manutenção.",
      features: [
        "Atribuir equipamento a um técnico.",
        "Log de manutenção.",
        "Gasolina/quilometragem (roadmap).",
      ],
    },

    financials: {
      title: "Financials (Relatórios)",
      path: "/financials",
      overview: "Relatórios financeiros de alto nível — receita, despesa, saldo em caixa. Puxa de invoices, POs, ledger.",
      features: [
        "DRE por mês.",
        "Entrada vs saída de caixa.",
        "Receita por cliente.",
      ],
    },

    finance: {
      title: "Finance (Cash Book & Ledger)",
      path: "/finance",
      overview: "O coração transacional da contabilidade — bank accounts, ledger, cash book. Todo '+ Pay' do Purchasing cai aqui.",
      features: [
        "Múltiplas bank accounts (com saldo corrente).",
        "Lançamentos manuais no ledger.",
        "Reconciliação (roadmap).",
      ],
      tasks: [
        { title: "Adicionar nova bank account", steps: [
          "Finance → Bank Accounts → '+ New'.",
          "Nome, saldo inicial, moeda.",
          "Salve — a conta aparece em todo modal '+ Pay'.",
        ]},
      ],
    },

    clients: {
      title: "Clientes",
      path: "/clients",
      overview: "Cadastro de clientes. Um cliente pode ter várias properties (que têm units). Cada cliente tem login opcional no portal.",
      features: [
        "Contato + endereço de billing.",
        "Properties + units vinculadas.",
        "Portal do cliente habilita/desabilita.",
      ],
    },

    properties: {
      title: "Properties (Imóveis)",
      path: "/properties",
      overview: "Property representa um local físico — casa, prédio, condomínio. Podem ter 0 ou mais units (apartamentos, andares).",
      features: [
        "Endereço, cidade, ZIP.",
        "Owner (client) + tenant opcional.",
        "Units aninhadas.",
        "Importar do Excel.",
      ],
      tasks: [
        { title: "Cadastrar casa (sem units)", steps: [
          "Properties → 'New'.",
          "Endereço + cliente owner.",
          "Não adicione unit. Salve.",
          "Service Calls vão herdar direto o endereço dessa property.",
        ]},
      ],
    },

    daily_reports: {
      title: "Daily Reports",
      path: "(dentro de qualquer Service Call ou Project)",
      overview: "Daily Reports são o registro polido, client-ready, do trabalho de um dia. Puxam horas do time clock automaticamente, listam subcontratados, embutem fotos, formatam com AI, e produzem um PDF branded pra enviar ao cliente.",
      features: [
        "Busca de employees com horas do timeclock automáticas.",
        "Busca de subcontratados + horas + número de workers.",
        "Fotos: Camera + Library picker (compressão automática antes do upload).",
        "Gerador AI de narrativa (Anthropic).",
        "PDF branded (cabeçalho navy JMO + logo).",
        "Envio por email ao cliente.",
      ],
      tasks: [
        { title: "Produzir daily report", steps: [
          "Abra Service Call ou Project.",
          "Scroll até 'Daily Reports' → '+ Daily Report'.",
          "Escreva narrativa ou cole notas cruas.",
          "Adicione crew (auto-preenche horas) e subcontratados.",
          "Anexe até uma dúzia de fotos.",
          "'AI polish' → gerar PDF → email pro cliente.",
        ]},
      ],
      tips: [
        "Faça o daily antes de sair do site — reconstruir depois é muito mais difícil.",
        "Reports profissionais criam confiança e diminuem o ciclo de pagamento.",
      ],
    },

    map: {
      title: "Live Map",
      path: "/map",
      overview: "Mapa GPS ao vivo dos técnicos e jobs ativos. Opcional — requer o tracker no mobile app.",
      features: [
        "Marker do técnico atualiza a cada 30s.",
        "Pins de jobs coloridos por status.",
        "Clique num marker → abre o ticket.",
      ],
    },

    permissions: {
      title: "Permissões",
      path: "/permissions",
      overview: "Página só de admin pra estreitar/alargar acesso a módulos por role. Ex.: mudar 'crew:view' de PM para false esconde o item do sidebar sem deploy.",
      features: [
        "Matriz: roles × modules × actions (view, create, edit, delete).",
        "Reset pra defaults por role.",
        "Ao vivo — vale no próximo reload.",
      ],
    },

    settings: {
      title: "Configurações",
      path: "/settings",
      overview: "Configurações globais da empresa: dados do negócio (usados nos PDFs), numeração de invoice, timezone, defaults de notificação.",
      features: [
        "Nome / endereço / telefone (aparecem em Proposal e Payroll PDFs).",
        "Moeda e locale default.",
        "Templates de email.",
      ],
      tips: [
        "Mantenha o endereço da empresa atualizado — imprime em todo PDF que vai pro cliente.",
      ],
    },
  },
};
